// AGC: BottomNav - Bottom navigation component with dynamic accent color
// AGC: Uses CSS variables for theme-aware navigation bar
// AGC: Refactored to consume useMainNavItems + useMicPressGesture (shared with DesktopSidebar)
import React from 'react'
import { useTheme } from '../../context/ThemeContext'
import { Mic } from 'lucide-react'
import { useMicPressGesture } from '../../hooks/useMicPressGesture'
import { useMainNavItems } from './mainNavItems'
import type { NavTab, MainNavItem } from './mainNavItems'
import { useTranslation } from '../../hooks/useTranslation'

export type { NavTab }

interface BottomNavProps {
  active: NavTab
  onNavigate: (tab: NavTab) => void
  onMicPress?: () => void
  onMicLongPress?: () => void
  onMicLongPressEnd?: () => void
  onMicSlideCancel?: () => void
  isRecording?: boolean
  recordingMode?: 'idle' | 'tap' | 'hold'
  className?: string
}

const isTablet = import.meta.env.VITE_TABLET_MODE === 'true'

/**
 * AGC: BottomNav - Classic Navy theme bottom navigation
 * AGC: Tablet mode: taller bar, bigger icons/labels, 6 items (3+mic+3)
 */
export const BottomNav: React.FC<BottomNavProps> = ({
  active,
  onNavigate,
  onMicPress,
  onMicLongPress,
  onMicLongPressEnd,
  onMicSlideCancel,
  isRecording = false,
  recordingMode = 'idle',
  className = '',
}) => {
  const { t } = useTranslation()
  const { isDark } = useTheme()

  const allNavItems = useMainNavItems()

  // AGC: Split enabled items around mic center — 2/2 on phone, 3/3 on tablet
  const enabledItems = allNavItems.filter(item => item.enabled)
  const midpoint = Math.ceil(enabledItems.length / 2)
  const leftNavItems = enabledItems.slice(0, midpoint)
  const rightNavItems = enabledItems.slice(midpoint)

  const { isMicActive, isLongPressing, handlers: micHandlers } = useMicPressGesture({
    onPress: onMicPress,
    onLongPress: onMicLongPress,
    onLongPressEnd: onMicLongPressEnd,
    onSlideCancel: onMicSlideCancel,
  })

  // AGC: Tap mode hides everything; hold mode keeps mic button visible (finger stays on it)
  if (isRecording && recordingMode === 'tap') return null

  // AGC: Render nav button helper (navy styling)
  const renderNavButton = (item: MainNavItem) => {
    const isActive = active === item.id

    return (
      <button
        key={item.id}
        onClick={() => onNavigate(item.id)}
        className={`
          flex flex-col items-center justify-center
          ${isTablet ? 'py-2.5 px-5' : 'py-1.5 px-4'} rounded-lg
          transition-all duration-200
          relative
          ${isActive
            ? 'text-white bg-white/20'
            : 'text-white/60 hover:text-white/80 hover:bg-white/10 active:bg-white/30'
          }
        `.replace(/\s+/g, ' ').trim()}
        aria-label={item.label}
        aria-current={isActive ? 'page' : undefined}
      >
        {item.icon}
        <span className={`${isTablet ? 'text-sm' : 'text-xs'} mt-1 font-medium`}>{item.label}</span>

        {/* AGC: Active indicator dot - Brazilian yellow accent */}
        {isActive && (
          <span className="
            absolute -top-0.5 left-1/2 -translate-x-1/2
            w-1.5 h-1.5
            bg-[#FFDF00]
            rounded-full
            shadow-lg shadow-[#FFDF00]/50
          " />
        )}
      </button>
    )
  }

  return (
    <>
      {/* AGC: Floating Microphone Button (dynamic accent color) */}
      <button
        {...micHandlers}
        className={`
          fixed left-1/2 -translate-x-1/2 z-[85]
          ${isTablet ? 'w-16 h-16' : 'w-14 h-14'} rounded-full
          flex items-center justify-center
          transition-all duration-300 ease-out
          select-none touch-none
          focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
          ${isMicActive
            ? 'scale-90 shadow-lg'
            : isLongPressing
              ? 'scale-110 bg-red-500 shadow-xl shadow-red-500/25'
              : 'shadow-lg hover:scale-105 active:scale-95'
          }
        `.replace(/\s+/g, ' ').trim()}
        style={{
          bottom: `calc(max(env(safe-area-inset-bottom), 8px) + ${isTablet ? '52px' : '36px'})`,
          backgroundColor: isMicActive ? '#F59E0B' : isLongPressing ? undefined : isDark ? '#22c55e' : '#FFDF00',
        }}
        aria-label={t('common.voiceCommand')}
        data-testid="floating-mic-button"
      >
        <Mic
          className={`
            ${isTablet ? 'w-8 h-8' : 'w-6 h-6'}
            transition-all duration-300
            ${isMicActive
              ? 'scale-90 text-white'
              : isLongPressing
                ? 'scale-110 animate-pulse text-white'
                : `scale-100 ${isDark ? 'text-white' : 'text-slate-800'}`
            }
          `.replace(/\s+/g, ' ').trim()}
        />

        {/* AGC: Fold animation ring (tap) - uses dynamic accent color */}
        {isMicActive && (
          <span
            className="absolute inset-0 rounded-full border-2 animate-ping"
            style={{ animationDuration: '1s', borderColor: 'var(--accent-light-bg)' }}
          />
        )}

        {/* AGC: Recording indicator ring (long-press) */}
        {isLongPressing && (
          <span
            className="absolute inset-0 rounded-full border-2 border-white animate-pulse"
            style={{ animationDuration: '0.5s' }}
          />
        )}
      </button>

      {/* AGC: Bottom Navigation Bar — hidden during hold-mode recording */}
      {recordingMode !== 'hold' && <nav
        className={`
          fixed bottom-0 left-0 right-0 z-[80]
          shadow-[0_-1px_3px_rgba(0,0,0,0.1)]
          transition-colors duration-200
          ${className}
        `.replace(/\s+/g, ' ').trim()}
        style={{
          paddingBottom: 'max(env(safe-area-inset-bottom), 8px)',
          backgroundColor: isDark ? '#0f172a' : '#065F46',
        }}
      >
        <div className={`flex items-center justify-between ${isTablet ? 'py-4' : 'py-2'} px-4`}>
          {/* AGC: Left side nav items */}
          <div className="flex items-center gap-1">
            {leftNavItems.map(renderNavButton)}
          </div>

          {/* AGC: Center spacer for mic button */}
          <div className={isTablet ? 'w-24' : 'w-16'} />

          {/* AGC: Right side nav items */}
          <div className="flex items-center gap-1">
            {rightNavItems.map(renderNavButton)}
          </div>
        </div>
      </nav>}
    </>
  )
}

export default BottomNav
